
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { WaitlistForm } from "./WaitlistForm";
import { useState } from "react";

/**
 * Hero Component
 * 
 * Main landing page hero section featuring:
 * - Powerful brand tagline "Start with cents. Grow with Centse."
 * - Supporting empowering message about financial journeys
 * - Call-to-action buttons for sign up and login
 * - Animated decorative elements for visual interest
 * 
 * @returns {JSX.Element} Hero section component
 */
export const Hero = () => {
  const navigate = useNavigate();
  const [showWaitlist, setShowWaitlist] = useState(false);
  
  return (
    <div className="relative overflow-hidden bg-background pt-16 pb-32">
      {/* Decorative elements for visual depth */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -right-4 w-64 h-64 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-64 h-64 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 w-64 h-64 bg-gradient-to-br from-secondary/20 to-accent/20 rounded-full blur-3xl" />
      </div>

      <div className="container relative">
        <div className="mx-auto max-w-4xl text-center">
          {/* Centse Logo */}
          <div className="mb-8 flex justify-center animate-fade-in">
            <img 
              src="/lovable-uploads/be0ebddb-c970-4098-9d03-1c7868fa0b0c.png" 
              alt="Centse - Your Financial Learning Companion" 
              className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 object-contain drop-shadow-lg hover:scale-105 transition-transform duration-300"
            />
          </div>
          
          {/* Main brand tagline with powerful gradient effect */}
          <h1 className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent text-5xl font-bold tracking-tight sm:text-7xl lg:text-8xl animate-fade-in">
            Start with cents.
            <br />
            Grow with Centse.
          </h1>
          
          {/* Clear value proposition - what the product is and who it's for */}
          <p className="mt-6 text-xl text-gray-600 animate-fade-in delay-100">
            <strong>Centse is a gamified financial education platform for Gen Z</strong> — from your first penny to your first paycheck, we've got you.
          </p>

          {/* Supporting empowering message */}
          <p className="mt-4 text-lg text-gray-500 animate-fade-in delay-200">
            Make money management addictive, not fearful. Every lesson feels like a game. 💪
          </p>

          {/* Conditional rendering: Show waitlist form or single focused CTA */}
          {!showWaitlist ? (
            <div className="mt-10 flex flex-col items-center justify-center">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-gradient-to-r from-primary via-primary/90 to-primary hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 group px-8 py-4 text-lg"
                onClick={() => setShowWaitlist(true)}
              >
                JOIN WAITLIST 
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              
              {/* Subtle hint for existing users - mobile only */}
              <p className="mt-4 text-sm text-muted-foreground md:hidden">
                Already have an account? Check the menu above ↗️
              </p>
            </div>
          ) : (
            <div className="mt-10 animate-fade-in">
              <WaitlistForm />
              <div className="mt-4 text-center">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowWaitlist(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ← Back
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
